import React, { useEffect, useState } from "react";
import { X, Save, History, Pencil } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * DreamEditModal — small, warm editor for the user's dream / north star.
 * Handles PATCH /me/dream and shows GET /me/dream/history.
 */
export default function DreamEditModal({ open, onClose, initial, onSaved }) {
  const [form, setForm] = useState({ full_text: "", north_star_short: "", target_uni: "", target_grades: "" });
  const [busy, setBusy] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    if (open && initial) {
      setForm({
        full_text: initial.full_text || "",
        north_star_short: initial.north_star_short || "",
        target_uni: initial.target_uni || "",
        target_grades: initial.target_grades || "",
      });
      setShowHistory(false);
    }
  }, [open, initial]);

  const openHistory = async () => {
    setShowHistory(true);
    setHistoryLoading(true);
    try {
      const { data } = await api.get("/me/dream/history");
      setHistory(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setHistoryLoading(false);
    }
  };

  const save = async () => {
    if (!form.north_star_short.trim()) {
      toast.error("A north star needs at least one line.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.patch("/me/dream", {
        full_text: form.full_text,
        north_star_short: form.north_star_short,
        target_uni: form.target_uni || null,
        target_grades: form.target_grades || null,
      });
      toast.success("Dream updated.");
      onSaved && onSaved(data);
      onClose && onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "color-mix(in oklab, var(--text) 40%, transparent)", backdropFilter: "blur(4px)" }}
      onClick={onClose}
      data-testid="dream-edit-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-xl rounded-3xl fade-up"
        style={{
          background: "var(--surface)",
          border: "1px solid var(--border)",
          maxHeight: "90vh",
          overflow: "auto",
        }}
      >
        <div className="p-6 sm:p-7">
          <div className="flex items-center justify-between mb-4">
            <div>
              <span className="eyebrow">rewrite your dream</span>
              <div className="font-display text-2xl leading-tight mt-1" style={{ color: "var(--text)" }}>
                {showHistory ? "How my dream has grown" : "Your north star"}
              </div>
            </div>
            <button onClick={onClose} className="btn-ghost !p-2" data-testid="dream-modal-close" aria-label="Close">
              <X size={16} strokeWidth={1.5} />
            </button>
          </div>

          {!showHistory ? (
            <>
              <div className="space-y-4">
                <div>
                  <label className="text-xs uppercase tracking-wider text-muted mb-1 block">north star (one line)</label>
                  <input
                    className="input-soft"
                    value={form.north_star_short}
                    onChange={(e) => setForm({ ...form, north_star_short: e.target.value })}
                    maxLength={200}
                    data-testid="dream-north-star-input"
                    placeholder="Psychology & neuroscience — because how we feel is who we become."
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-muted mb-1 block">the longer version</label>
                  <textarea
                    className="input-soft"
                    value={form.full_text}
                    onChange={(e) => setForm({ ...form, full_text: e.target.value })}
                    maxLength={4000}
                    rows={4}
                    data-testid="dream-full-text-input"
                    placeholder="I want to study psychology and neuroscience because…"
                    style={{ minHeight: 120 }}
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs uppercase tracking-wider text-muted mb-1 block">target university (optional)</label>
                    <input
                      className="input-soft"
                      value={form.target_uni || ""}
                      onChange={(e) => setForm({ ...form, target_uni: e.target.value })}
                      maxLength={200}
                      data-testid="dream-target-uni-input"
                      placeholder="UCL"
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-muted mb-1 block">target grades (optional)</label>
                    <input
                      className="input-soft"
                      value={form.target_grades || ""}
                      onChange={(e) => setForm({ ...form, target_grades: e.target.value })}
                      maxLength={200}
                      data-testid="dream-target-grades-input"
                      placeholder="A*A*A"
                    />
                  </div>
                </div>
              </div>

              <div className="mt-6 flex flex-col sm:flex-row justify-between items-center gap-3">
                <button
                  onClick={openHistory}
                  className="text-xs flex items-center gap-1.5 italic"
                  style={{ color: "var(--primary)" }}
                  data-testid="dream-history-link"
                >
                  <History size={12} strokeWidth={1.5} /> how my dream has grown
                </button>
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <button onClick={onClose} className="btn-ghost text-sm flex-1 sm:flex-none" data-testid="dream-cancel-btn">Cancel</button>
                  <button
                    onClick={save}
                    disabled={busy}
                    className="btn-primary text-sm flex items-center gap-2 flex-1 sm:flex-none disabled:opacity-60"
                    data-testid="dream-save-btn"
                  >
                    <Save size={14} strokeWidth={1.5} /> {busy ? "Saving…" : "Save"}
                  </button>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-1" data-testid="dream-history-list">
                {historyLoading && <div className="text-muted text-sm">Loading…</div>}
                {!historyLoading && history.length === 0 && (
                  <div className="italic text-muted text-sm">
                    Nothing here yet — your dream hasn't been rewritten. When it grows, it will live here.
                  </div>
                )}
                {!historyLoading && history.map((h, i) => (
                  <article
                    key={i}
                    className="rounded-2xl p-4"
                    style={{ background: "var(--surface-2)", border: "1px solid var(--border)" }}
                  >
                    <div className="text-[10px] uppercase tracking-wider text-muted mb-1">
                      until {(h.replaced_at || "").slice(0, 10)}
                    </div>
                    {h.north_star_short && (
                      <div className="font-display text-base italic leading-snug" style={{ color: "var(--text)" }}>
                        "{h.north_star_short}"
                      </div>
                    )}
                    {h.full_text && (
                      <div className="text-sm text-muted mt-2 leading-relaxed">{h.full_text}</div>
                    )}
                    {(h.target_uni || h.target_grades) && (
                      <div className="text-xs text-muted mt-2 flex gap-2 flex-wrap">
                        {h.target_uni && <span>{h.target_uni}</span>}
                        {h.target_grades && <span>· {h.target_grades}</span>}
                      </div>
                    )}
                  </article>
                ))}
              </div>
              <div className="mt-5 flex justify-end">
                <button
                  onClick={() => setShowHistory(false)}
                  className="btn-ghost text-sm"
                  data-testid="dream-history-back"
                >
                  ← back to editing
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/** Small pencil button — designed to sit on top-right of the north-star banner. */
export function DreamEditButton({ onClick, testid = "dream-edit-btn" }) {
  return (
    <button
      onClick={onClick}
      className="absolute top-4 right-4 sm:top-5 sm:right-6 w-9 h-9 rounded-full flex items-center justify-center transition-opacity opacity-70 sm:opacity-0 group-hover:opacity-100 focus:opacity-100"
      style={{
        background: "color-mix(in oklab, var(--surface) 85%, transparent)",
        border: "1px solid var(--border)",
        color: "var(--primary)",
        backdropFilter: "blur(6px)",
      }}
      aria-label="Edit your dream"
      title="Edit your dream"
      data-testid={testid}
    >
      <Pencil size={14} strokeWidth={1.75} />
    </button>
  );
}
